import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FaTruckMoving, FaSearch, FaSpinner, FaCheckCircle, FaTimes, FaArrowRight,
    FaExclamationTriangle, FaInfoCircle, FaEdit, FaSave, FaUser,
    FaMapMarkerAlt, FaCalendarAlt, FaTag, FaHistory,
} from 'react-icons/fa';

const MOVE_ENDPOINT = 'https://api.peaknet.com.au/move';

const STATUS_OPTIONS = [
    { value: 'requested',         label: 'Requested',         tone: 'amber' },
    { value: 'approved',          label: 'Approved',          tone: 'blue' },
    { value: 'provisioning',      label: 'Provisioning',      tone: 'blue' },
    { value: 'cease_scheduled',   label: 'Cease scheduled',   tone: 'indigo' },
    { value: 'active_at_new',     label: 'Active at new',     tone: 'green' },
    { value: 'cancelled',         label: 'Cancelled',         tone: 'gray' },
    { value: 'failed',            label: 'Failed',            tone: 'red' },
];

// Gruvbox-dark tone palette — matches the rest of the admin portal.
const toneClass = (tone) => ({
    amber:  'bg-[#3c3836] text-[#fabd2f] border border-[#d79921]',
    blue:   'bg-[#3c3836] text-[#83a598] border border-[#458588]',
    indigo: 'bg-[#3c3836] text-[#83a598] border border-[#458588]',
    green:  'bg-[#3c3836] text-[#b8bb26] border border-[#98971a]',
    gray:   'bg-[#3c3836] text-[#bdae93] border border-[#504945]',
    red:    'bg-[#3c3836] text-[#fb4934] border border-[#cc241d]',
}[tone] || 'bg-[#3c3836] text-[#bdae93] border border-[#504945]');

const callApi = async (body) => {
    const r = await fetch(MOVE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
        body: JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-AU', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
            timeZone: 'Australia/Sydney',
        });
    } catch { return iso; }
};
const formatDateOnly = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-AU', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        });
    } catch { return iso; }
};

const MoveRequestsManagement = () => {
    const [statusFilter, setStatusFilter] = useState('requested');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [searchQ, setSearchQ] = useState('');

    const loadList = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await callApi({ action: 'adminList', status: statusFilter, limit: 100 });
            setItems(r.items || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { loadList(); }, [loadList]);

    const filteredItems = useMemo(() => {
        if (!searchQ.trim()) return items;
        const q = searchQ.toLowerCase().trim();
        return items.filter((it) =>
            (it.customerEmail || '').toLowerCase().includes(q)
            || (it.customerName || '').toLowerCase().includes(q)
            || (it.moveId || '').toLowerCase().includes(q),
        );
    }, [items, searchQ]);

    const refreshSelected = useCallback(async () => {
        if (!selected) return;
        try {
            const r = await callApi({
                action: 'adminGet',
                customerUID: selected.customerUID,
                moveId: selected.moveId,
            });
            setSelected(r.item);
            await loadList();
        } catch (e) {
            setError(e.message);
        }
    }, [selected, loadList]);

    return (
        <div className="p-6 max-w-7xl mx-auto bg-[#1d2021] min-h-screen">
            <div className="flex items-center gap-3 mb-6">
                <FaTruckMoving className="text-3xl text-[#fabd2f]" />
                <div>
                    <h1 className="text-2xl font-bold text-[#ebdbb2]">Move Requests</h1>
                    <p className="text-sm text-[#928374]">Customer-initiated move-home requests. Every state change is manual.</p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-5">
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-sm font-medium text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
                >
                    {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value} className="bg-[#282828]">{s.label}</option>
                    ))}
                </select>
                <div className="relative flex-1 max-w-md">
                    <FaSearch className="absolute left-3 top-3 text-[#928374]" />
                    <input
                        type="text"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        placeholder="Filter by email, name, or move ID…"
                        className="w-full pl-10 pr-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-sm text-[#ebdbb2] placeholder-[#928374] focus:outline-none focus:border-[#fabd2f]"
                    />
                </div>
                <button
                    onClick={loadList}
                    disabled={loading}
                    className="px-4 py-2 bg-[#282828] hover:bg-[#3c3836] text-[#ebdbb2] rounded-lg text-sm font-medium border border-[#3c3836] disabled:opacity-50"
                >
                    {loading ? <FaSpinner className="animate-spin" /> : 'Refresh'}
                </button>
                <span className="ml-auto text-sm text-[#928374]">
                    {filteredItems.length} request{filteredItems.length === 1 ? '' : 's'}
                </span>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-[#3c3836] border border-[#fb4934] rounded-lg text-sm text-[#fb4934]">
                    <FaExclamationTriangle className="inline mr-2" />{error}
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#928374]">
                    <FaSpinner className="animate-spin text-2xl" />
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 bg-[#282828] rounded-xl border border-[#3c3836]">
                    <FaInfoCircle className="text-3xl text-[#928374] mx-auto mb-2" />
                    <p className="text-[#928374]">No move requests with status "{statusFilter}".</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredItems.map((item) => (
                        <button
                            key={item.moveId}
                            onClick={() => setSelected(item)}
                            className="w-full text-left bg-[#282828] border border-[#3c3836] hover:border-[#fabd2f] hover:shadow-lg transition rounded-xl p-4 flex items-center gap-4"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-[#ebdbb2]">{item.customerName || item.customerEmail}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${toneClass(STATUS_OPTIONS.find((s) => s.value === item.status)?.tone)}`}>
                                        {item.status}
                                    </span>
                                </div>
                                <div className="text-xs text-[#bdae93] truncate">
                                    {item.oldAddress} → <strong className="text-[#ebdbb2]">{item.newAddress}</strong>
                                </div>
                                <div className="text-xs text-[#928374] mt-1">
                                    Move: {formatDateOnly(item.moveDate)} · Tech: {item.newAccessTech}{item.planChanged ? ` · Plan: ${item.oldPlan} → ${item.newPlan}` : ''}
                                </div>
                            </div>
                            <FaArrowRight className="text-[#928374]" />
                        </button>
                    ))}
                </div>
            )}

            {selected && (
                <MoveDetail
                    item={selected}
                    onClose={() => setSelected(null)}
                    onRefresh={refreshSelected}
                />
            )}
        </div>
    );
};

// ───────────────────────────── detail drawer ─────────────────────────────

const MoveDetail = ({ item, onClose, onRefresh }) => {
    const [editing, setEditing] = useState(false);
    const [edit, setEdit] = useState({
        moveDate: item.moveDate || '',
        newAvcId: item.newAvcId || '',
        ceaseTransactionId: item.ceaseTransactionId || '',
        createTransactionId: item.createTransactionId || '',
        newPlan: item.newPlan || '',
        newMonthlyFee: item.newMonthlyFee ?? '',
        notes: item.notes || '',
    });
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState('');
    const [planOptions, setPlanOptions] = useState([]);

    // Load plans eligible at the new tech as soon as we enter edit mode, so
    // admin gets a sensible dropdown rather than a free-text input. Each plan
    // entry carries its standard monthlyFee — when admin picks a plan we
    // auto-fill newMonthlyFee unless they manually override it after.
    useEffect(() => {
        if (!editing || planOptions.length) return;
        (async () => {
            try {
                const r = await callApi({ action: 'adminListPlans', accessTech: item.newAccessTech });
                setPlanOptions(r.plans || []);
            } catch (e) {
                console.warn('adminListPlans failed:', e.message);
            }
        })();
    }, [editing, item.newAccessTech, planOptions.length]);

    const handlePlanChange = (planName) => {
        const pick = planOptions.find((p) => p.planName === planName);
        setEdit((prev) => ({
            ...prev,
            newPlan: planName,
            newMonthlyFee: pick ? pick.monthlyFee : prev.newMonthlyFee,
        }));
    };

    const transition = useCallback(async (action, extra = {}) => {
        if (action === 'adminReject' || action === 'adminCancel') {
            const reason = prompt(`Reason (${action.replace('admin', '').toLowerCase()})?`);
            if (reason === null) return;
            extra.failReason = reason;
        }
        setBusy(true);
        setActionError('');
        try {
            await callApi({
                action,
                customerUID: item.customerUID,
                moveId: item.moveId,
                actor: 'admin',
                ...extra,
            });
            await onRefresh();
        } catch (e) {
            setActionError(e.message);
        } finally {
            setBusy(false);
        }
    }, [item, onRefresh]);

    const saveEdits = useCallback(async () => {
        setBusy(true);
        setActionError('');
        try {
            // Derive planChanged + planAction from the edits if admin picked a plan.
            // If admin changed newPlan to something different from oldPlan, mark
            // it as 'customer_choice' (since admin is overriding the auto-pick)
            // so the UI shows the right badge.
            const updates = { ...edit };
            if (edit.newPlan && edit.newPlan !== item.oldPlan) {
                updates.planChanged = true;
                updates.planAction = 'customer_choice';
            } else if (edit.newPlan && edit.newPlan === item.oldPlan) {
                updates.planChanged = false;
                updates.planAction = 'unchanged';
            }
            // Drop empty-string monthlyFee so it doesn't overwrite with NaN.
            if (updates.newMonthlyFee === '') delete updates.newMonthlyFee;
            await callApi({
                action: 'adminUpdate',
                customerUID: item.customerUID,
                moveId: item.moveId,
                updates,
                actor: 'admin',
            });
            await onRefresh();
            setEditing(false);
        } catch (e) {
            setActionError(e.message);
        } finally {
            setBusy(false);
        }
    }, [edit, item, onRefresh]);

    const status = item.status;
    const transitions = {
        requested: [
            { label: 'Approve', action: 'adminApprove', kind: 'primary' },
            { label: 'Reject', action: 'adminReject', kind: 'danger' },
            { label: 'Cancel', action: 'adminCancel', kind: 'subtle' },
        ],
        approved: [
            { label: 'Start provisioning', action: 'adminMarkProvisioning', kind: 'primary' },
            { label: 'Cancel', action: 'adminCancel', kind: 'subtle' },
        ],
        provisioning: [
            { label: 'Mark cease scheduled', action: 'adminMarkCeased', kind: 'primary' },
            { label: 'Mark failed', action: 'adminReject', kind: 'danger' },
            { label: 'Cancel', action: 'adminCancel', kind: 'subtle' },
        ],
        cease_scheduled: [
            { label: 'Mark active at new', action: 'adminMarkActive', kind: 'primary' },
            { label: 'Mark failed', action: 'adminReject', kind: 'danger' },
            { label: 'Cancel', action: 'adminCancel', kind: 'subtle' },
        ],
        active_at_new: [],
        cancelled: [],
        failed: [],
    }[status] || [];

    const buttonClass = (kind) => ({
        primary: 'bg-[#fabd2f] hover:bg-[#d79921] text-[#1d2021]',
        danger: 'bg-[#fb4934] hover:bg-[#cc241d] text-[#1d2021]',
        subtle: 'bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] border border-[#504945]',
    }[kind]);

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-full max-w-2xl bg-[#1d2021] h-full overflow-y-auto shadow-2xl border-l border-[#3c3836]">
                <div className="sticky top-0 bg-[#1d2021] z-10 border-b border-[#3c3836] p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${toneClass(STATUS_OPTIONS.find((s) => s.value === status)?.tone)}`}>
                            {status}
                        </span>
                        <code className="text-sm text-[#bdae93]">{item.moveId}</code>
                    </div>
                    <button onClick={onClose} className="text-[#928374] hover:text-[#ebdbb2]"><FaTimes /></button>
                </div>

                <div className="p-6 space-y-6">
                    {actionError && (
                        <div className="p-3 bg-[#3c3836] border border-[#fb4934] rounded-lg text-sm text-[#fb4934]">
                            <FaExclamationTriangle className="inline mr-2" />{actionError}
                        </div>
                    )}

                    <Section icon={<FaUser />} title="Customer">
                        <KV label="Name" value={item.customerName} />
                        <KV label="Email" value={item.customerEmail} />
                        <KV label="UID" value={<code className="text-xs text-[#bdae93]">{item.customerUID}</code>} />
                    </Section>

                    <Section icon={<FaMapMarkerAlt />} title="Addresses">
                        <KV label="Old address" value={item.oldAddress || '—'} />
                        <KV label="Old tech" value={item.oldAccessTech} />
                        <KV label="Old AVC" value={item.oldAvcId || '—'} />
                        <KV label="New address" value={item.newAddress} />
                        <KV label="New tech" value={item.newAccessTech} />
                        <KV label="New AVC" value={
                            editing ? (
                                <Input
                                    value={edit.newAvcId}
                                    onChange={(v) => setEdit({ ...edit, newAvcId: v })}
                                    placeholder="AVC ID once Vocus creates"
                                />
                            ) : (item.newAvcId || '—')
                        } />
                    </Section>

                    <Section icon={<FaCalendarAlt />} title="Schedule">
                        <KV label="Move date" value={
                            editing ? (
                                <Input type="date" value={edit.moveDate} onChange={(v) => setEdit({ ...edit, moveDate: v })} />
                            ) : formatDateOnly(item.moveDate)
                        } />
                        <KV label="Cease tx ID" value={
                            editing ? <Input mono value={edit.ceaseTransactionId} onChange={(v) => setEdit({ ...edit, ceaseTransactionId: v })} placeholder="Vocus FIBRE::Delete tx" /> : (item.ceaseTransactionId || '—')
                        } />
                        <KV label="Create tx ID" value={
                            editing ? <Input mono value={edit.createTransactionId} onChange={(v) => setEdit({ ...edit, createTransactionId: v })} placeholder="Vocus FIBRE::Create tx" /> : (item.createTransactionId || '—')
                        } />
                    </Section>

                    <Section icon={<FaTag />} title="Plan">
                        <KV label="Old plan" value={`${item.oldPlan || '—'}${item.oldMonthlyFee != null ? ` ($${Number(item.oldMonthlyFee).toFixed(2)}/mo)` : ''}`} />
                        <KV label="New plan" value={
                            editing ? (
                                <select
                                    value={edit.newPlan}
                                    onChange={(e) => handlePlanChange(e.target.value)}
                                    className="px-2 py-1 bg-[#282828] border border-[#3c3836] rounded text-sm text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f] min-w-[260px]"
                                >
                                    <option value="">— pick a plan —</option>
                                    {planOptions.map((p) => (
                                        <option key={p.planName} value={p.planName} className="bg-[#282828]">
                                            {p.planName} · {p.speed} · ${p.monthlyFee.toFixed(2)}/mo
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span>
                                    {item.newPlan || '—'}
                                    {item.planChanged && item.oldPlan && (
                                        <span className="text-xs text-[#928374] ml-2">
                                            (was <s>{item.oldPlan}</s>)
                                        </span>
                                    )}
                                </span>
                            )
                        } />
                        <KV label="New monthly fee" value={
                            editing ? (
                                <Input
                                    type="number"
                                    value={edit.newMonthlyFee}
                                    onChange={(v) => setEdit({ ...edit, newMonthlyFee: v === '' ? '' : Number(v) })}
                                    placeholder="79.99"
                                />
                            ) : (
                                item.newMonthlyFee != null ? `$${Number(item.newMonthlyFee).toFixed(2)}` : '—'
                            )
                        } />
                        {item.planAction && item.planAction !== 'unchanged' && (
                            <KV label="Plan action" value={
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    item.planAction === 'auto_upgrade' ? 'bg-[#3c3836] text-[#fabd2f] border border-[#d79921]' :
                                    item.planAction === 'auto_downgrade' ? 'bg-[#3c3836] text-[#83a598] border border-[#458588]' :
                                    item.planAction === 'customer_choice' ? 'bg-[#3c3836] text-[#b8bb26] border border-[#98971a]' :
                                    'bg-[#3c3836] text-[#fb4934] border border-[#cc241d]'
                                }`}>
                                    {item.planAction === 'auto_upgrade' ? '↑ Free upgrade (same/lower price, better speed)' :
                                     item.planAction === 'auto_downgrade' ? '↓ Adjusted to fit new tech (price never goes up)' :
                                     item.planAction === 'customer_choice' ? '👤 Customer picked' :
                                     `⚠ ${item.planAction}`}
                                </span>
                            } />
                        )}
                        <KV label="Promo end" value={item.promoEndDate ? formatDateOnly(item.promoEndDate) : '—'} />
                    </Section>

                    <Section icon={<FaInfoCircle />} title="Admin notes">
                        {editing ? (
                            <textarea
                                value={edit.notes}
                                onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-sm text-[#ebdbb2] placeholder-[#928374]"
                                placeholder="Internal notes — not shown to customer"
                            />
                        ) : (
                            <p className="text-sm text-[#bdae93] whitespace-pre-wrap">{item.notes || '—'}</p>
                        )}
                    </Section>

                    {Array.isArray(item.auditLog) && item.auditLog.length > 0 && (
                        <Section icon={<FaHistory />} title="Audit log">
                            <ul className="space-y-2 text-xs text-[#bdae93]">
                                {[...item.auditLog].reverse().map((entry, idx) => (
                                    <li key={idx} className="flex gap-3 border-l-2 border-[#458588] pl-3">
                                        <span className="text-[#928374] shrink-0 w-32">{formatDate(entry.at)}</span>
                                        <span className="flex-1 text-[#ebdbb2]">
                                            <strong>{entry.action}</strong>
                                            {entry.actor && ` · ${entry.actor}`}
                                            {entry.from && ` (from ${entry.from})`}
                                            {entry.failReason && ` — ${entry.failReason}`}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    <div className="border-t border-[#3c3836] pt-5">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            {editing ? (
                                <>
                                    <button
                                        onClick={saveEdits}
                                        disabled={busy}
                                        className="px-4 py-2 bg-[#b8bb26] hover:bg-[#98971a] text-[#1d2021] rounded-lg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {busy ? <FaSpinner className="animate-spin" /> : <FaSave />} Save edits
                                    </button>
                                    <button
                                        onClick={() => { setEditing(false); setEdit({
                                            moveDate: item.moveDate || '',
                                            newAvcId: item.newAvcId || '',
                                            ceaseTransactionId: item.ceaseTransactionId || '',
                                            createTransactionId: item.createTransactionId || '',
                                            newPlan: item.newPlan || '',
                                            newMonthlyFee: item.newMonthlyFee ?? '',
                                            notes: item.notes || '',
                                        }); }}
                                        className="px-4 py-2 border border-[#3c3836] bg-[#282828] hover:bg-[#3c3836] text-[#ebdbb2] rounded-lg text-sm font-semibold"
                                    >Cancel</button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setEditing(true)}
                                    className="px-4 py-2 border border-[#3c3836] bg-[#282828] hover:bg-[#3c3836] text-[#ebdbb2] rounded-lg text-sm font-semibold inline-flex items-center gap-2"
                                >
                                    <FaEdit /> Edit fields
                                </button>
                            )}
                            {transitions.map((t) => (
                                <button
                                    key={t.action}
                                    onClick={() => transition(t.action)}
                                    disabled={busy || editing}
                                    className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 ${buttonClass(t.kind)}`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        {transitions.length === 0 && !editing && (
                            <p className="text-xs text-[#928374]">No further actions — this request is in a terminal state.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── small helpers ───
const Section = ({ icon, title, children }) => (
    <div>
        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#fabd2f]">
            <span className="text-[#928374]">{icon}</span> {title}
        </div>
        <div className="space-y-1 text-sm text-[#ebdbb2] pl-6">{children}</div>
    </div>
);

const KV = ({ label, value }) => (
    <div className="flex gap-3">
        <span className="text-[#928374] w-32 shrink-0">{label}</span>
        <span className="flex-1 text-[#ebdbb2]">{value}</span>
    </div>
);

const Input = ({ value, onChange, placeholder, type = 'text', mono }) => (
    <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`px-2 py-1 bg-[#282828] border border-[#3c3836] rounded text-sm text-[#ebdbb2] placeholder-[#928374] focus:outline-none focus:border-[#fabd2f] ${mono ? 'font-mono' : ''}`}
    />
);

export default MoveRequestsManagement;
